import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, formatRON, devWarn } from "../lib/api";
import { invoiceToFormState } from "../lib/invoiceForm";
import {
  ColorPicker,
  collectAllColors,
  colorPurchasePrice,
  applicableColors,
  selKey,
  colorSlug,
  MAT_COLOR,
} from "../components/ColorPicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { FileDown, Folder, Percent, Search, RotateCcw, Plus, X, Wallet, UserCog, Save } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import { ClientAutocomplete } from "../components/ClientAutocomplete";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const NO_CATEGORY = "__none__";

// Build the auto-prefix shown in the Note column for sheet-based items:
// "Lungime*Coli: 1.5*4 + 2*3" — exactly like the user requested.
function buildSheetsAutoNote(sheets) {
  const valid = (Array.isArray(sheets) ? sheets : []).filter(
    (s) => Number(s.length || 0) > 0 && Number(s.count || 0) > 0
  );
  if (!valid.length) return "";
  const parts = valid.map((s) => `${s.length}*${s.count}`).join(" + ");
  return `Lungime*Coli: ${parts}`;
}

function combineNotes(autoNote, userNote) {
  const a = (autoNote || "").trim();
  const u = (userNote || "").trim();
  if (a && u) return `${a} | ${u}`;
  return a || u;
}

// Compute actual invoice quantity for a material given its current selection state.
function computeQty(material, sel) {
  if (!sel) return 0;
  const hasWidth = Number(material.width_m || 0) > 0;
  if (!hasWidth) return Number(sel.quantity || 0);
  const mode = sel.mode || "sheets";
  if (mode === "direct") return Number(sel.direct_m2 || 0);
  const w = Number(material.width_m);
  const sheets = Array.isArray(sel.sheets) ? sel.sheets : [];
  const sum = sheets.reduce(
    (a, s) => a + Number(s.length || 0) * w * Number(s.count || 0),
    0
  );
  return Number(sum.toFixed(3));
}

export default function CalculatorPage() {
  const { user, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [materials, setMaterials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clientLastName, setClientLastName] = useState("");
  const [clientFirstName, setClientFirstName] = useState("");
  const [clientCui, setClientCui] = useState("");
  const [clientCnp, setClientCnp] = useState("");
  const [clientAddress, setClientAddress] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [clientRegCom, setClientRegCom] = useState("");
  const [delegateName, setDelegateName] = useState("");
  const [agentName, setAgentName] = useState("");
  const [savingAgent, setSavingAgent] = useState(false);
  const [discountPct, setDiscountPct] = useState("");
  const [advance, setAdvance] = useState("");
  const [includeCodes, setIncludeCodes] = useState(true);
  const [customNumber, setCustomNumber] = useState("");
  const [suggestedNumber, setSuggestedNumber] = useState(null);
  // selections["materialId::color"] -> { mode, sheets:[{length,count}], direct_m2, quantity, notes }
  const [selections, setSelections] = useState({});
  const [selectedColors, setSelectedColors] = useState([MAT_COLOR]);
  const [search, setSearch] = useState("");
  const [downloading, setDownloading] = useState(false);

  const allColors = useMemo(() => collectAllColors(materials), [materials]);
  const appColors = (m) => applicableColors(m, selectedColors);
  // Color sent to the backend: named color only for materials that define colors.
  const itemColor = (m, color) => ((m.colors || []).length > 0 ? color : "");

  const load = async ({ silent = false } = {}) => {
    try {
      const [m, c] = await Promise.all([api.get("/materials"), api.get("/categories")]);
      setMaterials(m.data);
      setCategories(c.data);
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Fetch the next auto-suggested document number so the user sees the
  // upcoming sequence as a placeholder ("Numar: 008" etc).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/next-number/factura");
        if (!cancelled) setSuggestedNumber(data?.number ?? null);
      } catch (err) {
        devWarn("next-number fetch failed (placeholder hint only):", err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pre-fill agent name from user profile (sticky default).
  useEffect(() => {
    if (user?.agent_name) setAgentName(user.agent_name);
  }, [user?.agent_name]);

  // Auto-fill from a saved deviz when navigated with ?from=<invoiceId>.
  // Runs after materials have loaded so we can correctly map material_id -> width mode.
  useEffect(() => {
    const fromId = searchParams.get("from");
    if (!fromId || materials.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: inv } = await api.get(`/invoices/${fromId}`);
        if (cancelled) return;
        const s = invoiceToFormState(inv, materials);
        setSelections(s.selections);
        setClientLastName(s.client.lastName);
        setClientFirstName(s.client.firstName);
        setClientCui(s.client.cui);
        setClientCnp(s.client.cnp);
        setClientAddress(s.client.address);
        setClientPhone(s.client.phone);
        setClientRegCom(s.client.regCom);
        setDelegateName(s.delegateName);
        // Note: agent_name stays the currently logged-in user (not overridden
        // from the deviz, per user's explicit request).
        setDiscountPct(s.discountPct);
        setAdvance(s.advance);
        setIncludeCodes(s.includeCodes);
        setSelectedColors(s.selectedColors || [MAT_COLOR]);
        toast.success(
          inv.type === "deviz"
            ? "Deviz preluat in factura. Adauga avans/reducere si descarca."
            : "Factura incarcata. Modifica si salveaza ca o noua factura."
        );
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
      } finally {
        // Drop the ?from param so a refresh doesn't re-import.
        if (!cancelled) setSearchParams({}, { replace: true });
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, materials, setSearchParams]);

  const saveAgentName = async () => {
    setSavingAgent(true);
    try {
      await api.put("/auth/me/agent-name", { agent_name: agentName });
      toast.success("Nume agent salvat");
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare");
    } finally {
      setSavingAgent(false);
    }
  };

  const pickClient = (c) => {
    setClientLastName(c.last_name || "");
    setClientFirstName(c.first_name || "");
    setClientCui(c.cui || "");
    setClientCnp(c.cnp || "");
    setClientAddress(c.address || "");
    setClientPhone(c.phone || "");
    setClientRegCom(c.reg_com || "");
  };

  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, []);

  const updateSel = (id, patch) => {
    setSelections((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  };

  const resetSelections = () => {
    if (!window.confirm("Sterge toate cantitatile introduse?")) return;
    setSelections({});
    setDiscountPct("");
    setAdvance("");
  };

  const categoriesById = useMemo(() => {
    const m = {};
    for (const c of categories) m[c.id] = c;
    return m;
  }, [categories]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filter = (m) =>
      !q ||
      m.name.toLowerCase().includes(q) ||
      (m.code || "").toLowerCase().includes(q);
    const order = categories.map((c) => c.id);
    const buckets = {};
    for (const id of order) buckets[id] = [];
    buckets[NO_CATEGORY] = [];
    for (const m of materials) {
      if (!filter(m)) continue;
      const k = m.category_id && buckets[m.category_id] ? m.category_id : NO_CATEGORY;
      buckets[k].push(m);
    }
    const result = order
      .filter((id) => buckets[id].length > 0)
      .map((id) => ({ id, name: categoriesById[id]?.name || "—", items: buckets[id] }));
    if (buckets[NO_CATEGORY].length > 0) {
      result.push({ id: NO_CATEGORY, name: "Fara categorie", items: buckets[NO_CATEGORY] });
    }
    return result;
  }, [materials, categories, categoriesById, search]);

  const invoice = useMemo(() => {
    const rows = [];
    let subtotal = 0;
    for (const m of materials) {
      for (const color of applicableColors(m, selectedColors)) {
        const sel = selections[selKey(m.id, color)];
        const qty = computeQty(m, sel);
        if (qty <= 0) continue;
        const unitPrice = colorPurchasePrice(m, color) * (1 + Number(m.markup) / 100);
        const lineTotal = unitPrice * qty;
        subtotal += lineTotal;
        // Build the dimensions auto-note when in sheets mode for width-based materials.
        const hasWidth = Number(m.width_m || 0) > 0;
        const inSheetsMode = hasWidth && (sel?.mode || "sheets") !== "direct";
        const autoNote = inSheetsMode ? buildSheetsAutoNote(sel?.sheets) : "";
        const userNote = (sel?.notes || "").trim();
        rows.push({
          material_id: m.id,
          color: itemColor(m, color),
          name: m.name,
          unit: m.unit || "",
          quantity: qty,
          unit_price: unitPrice,
          line_total: lineTotal,
          notes: combineNotes(autoNote, userNote),
        });
      }
    }
    const pctRaw = Number(discountPct || 0);
    const pct = Math.max(0, Math.min(100, isNaN(pctRaw) ? 0 : pctRaw));
    const discountValue = (subtotal * pct) / 100;
    const total = subtotal - discountValue;
    let adv = Math.max(0, Number(advance || 0) || 0);
    if (adv > total) adv = total;
    const due = total - adv;
    return {
      rows,
      subtotal,
      discount_pct: pct,
      discount_value: discountValue,
      total,
      advance: adv,
      due,
      hasSelected: rows.length > 0,
    };
  }, [materials, selections, selectedColors, discountPct, advance]);

  const downloadPdf = async () => {
    if (!invoice.hasSelected) {
      toast.error("Introdu cantitatea pentru cel putin un material");
      return;
    }
    setDownloading(true);
    try {
      const items = [];
      for (const m of materials) {
        for (const color of applicableColors(m, selectedColors)) {
          const sel = selections[selKey(m.id, color)];
          const qty = computeQty(m, sel);
          if (qty > 0) {
            const hasWidth = Number(m.width_m || 0) > 0;
            const inSheetsMode = hasWidth && (sel?.mode || "sheets") !== "direct";
            const autoNote = inSheetsMode ? buildSheetsAutoNote(sel?.sheets) : "";
            const finalNotes = combineNotes(autoNote, sel?.notes);
            items.push({
              material_id: m.id,
              quantity: qty,
              notes: finalNotes,
              color: itemColor(m, color),
            });
          }
        }
      }
      const body = {
        client_last_name: clientLastName,
        client_first_name: clientFirstName,
        client_cui: clientCui,
        client_cnp: clientCnp,
        client_address: clientAddress,
        client_phone: clientPhone,
        client_reg_com: clientRegCom,
        delegate_name: delegateName,
        agent_name: agentName,
        discount_pct: invoice.discount_pct,
        advance: invoice.advance,
        include_codes: includeCodes,
        items,
      };
      const parsedNum = parseInt(customNumber, 10);
      if (!isNaN(parsedNum) && parsedNum > 0) {
        body.number = parsedNum;
      }
      const res = await api.post(
        "/invoice/pdf",
        body,
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = `${clientLastName || ""}-${clientFirstName || ""}`
        .replace(/^-|-$/g, "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^A-Za-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      a.download = `factura-${slug || Date.now()}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Factura descarcata. Stocul a fost actualizat.");
      setCustomNumber("");
      // Refresh suggested next number after a successful submit.
      try {
        const { data } = await api.get("/next-number/factura");
        setSuggestedNumber(data?.number ?? null);
      } catch (err) { devWarn("next-number refresh failed:", err); }
      await load({ silent: true });
    } catch (e) {
      toast.error("Eroare la generarea facturii");
    } finally {
      setDownloading(false);
    }
  };

  // ---- Width-based material: render mode toggle + conditional inputs ----
  const renderWidthInput = (m, sk, tid) => {
    const sel = selections[sk] || {};
    const mode = sel.mode || "sheets";
    const w = Number(m.width_m);
    const sheets =
      Array.isArray(sel.sheets) && sel.sheets.length > 0
        ? sel.sheets
        : [{ length: "", count: "" }];
    const qty = computeQty(m, sel);

    const updateSheet = (i, patch) => {
      const copy = sheets.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
      updateSel(sk, { sheets: copy, mode: "sheets" });
    };
    const addSheet = () => {
      // Stable id so row keys survive add/remove without React state mix-ups.
      const id = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `s-${Date.now()}-${Math.random()}`;
      updateSel(sk, { sheets: [...sheets, { id, length: "", count: "" }], mode: "sheets" });
    };
    const removeSheet = (i) => {
      const copy = sheets.filter((_, idx) => idx !== i);
      updateSel(sk, { sheets: copy.length ? copy : [{ length: "", count: "" }] });
    };

    return (
      <div className="space-y-1.5">
        {/* Mode toggle */}
        <div className="flex rounded-sm border border-zinc-300 overflow-hidden w-full">
          <button
            type="button"
            onClick={() => updateSel(sk, { mode: "sheets" })}
            className={`flex-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition ${
              mode !== "direct"
                ? "bg-black text-white"
                : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
            data-testid={`mat-mode-sheets-${tid}`}
          >
            Coli
          </button>
          <button
            type="button"
            onClick={() => updateSel(sk, { mode: "direct" })}
            className={`flex-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider transition border-l border-zinc-300 ${
              mode === "direct"
                ? "bg-black text-white"
                : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
            data-testid={`mat-mode-direct-${tid}`}
          >
            m² direct
          </button>
        </div>

        {mode === "direct" ? (
          <Input
            type="number"
            min="0"
            step="0.01"
            value={sel.direct_m2 ?? ""}
            onChange={(e) => updateSel(sk, { direct_m2: e.target.value, mode: "direct" })}
            placeholder="m² total"
            className="h-8 rounded-sm border-zinc-300 focus-visible:ring-black text-sm"
            data-testid={`mat-direct-${tid}`}
          />
        ) : (
          <div className="space-y-1">
            {sheets.map((s, i) => (
              <div key={s.id || `idx-${i}`} className="flex gap-1 items-center">
                <Input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  value={s.length ?? ""}
                  onChange={(e) => updateSheet(i, { length: e.target.value })}
                  placeholder="Lungime"
                  className="h-10 min-w-[6rem] flex-1 px-2 rounded-sm border-zinc-300 focus-visible:ring-black text-base font-mono [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  data-testid={`mat-sheet-length-${tid}-${i}`}
                />
                <span className="text-zinc-500 text-sm shrink-0">×</span>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  inputMode="numeric"
                  value={s.count ?? ""}
                  onChange={(e) => updateSheet(i, { count: e.target.value })}
                  placeholder="Coli"
                  className="h-10 w-16 md:w-20 shrink-0 px-1 rounded-sm border-zinc-300 focus-visible:ring-black text-base font-mono text-center [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  data-testid={`mat-sheet-count-${tid}-${i}`}
                />
                {sheets.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeSheet(i)}
                    className="text-red-500 hover:text-red-700 p-1"
                    data-testid={`mat-sheet-remove-${tid}-${i}`}
                    title="Sterge"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addSheet}
              className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 hover:text-black flex items-center gap-1 py-0.5"
              data-testid={`mat-sheet-add-${tid}`}
            >
              <Plus size={10} /> Adauga lungime
            </button>
          </div>
        )}

        <div className="text-[10px] text-zinc-600 font-mono pt-0.5">
          latime {w}m · {qty > 0 ? `${qty} m²` : "—"}
        </div>
      </div>
    );
  };

  const renderMaterialRow = (m, color) => {
    const sk = selKey(m.id, color);
    const tid = color === MAT_COLOR ? m.id : `${m.id}-${colorSlug(color)}`;
    const sel = selections[sk] || {};
    const hasWidth = Number(m.width_m || 0) > 0;
    const unitPrice = colorPurchasePrice(m, color) * (1 + Number(m.markup) / 100);
    const qty = computeQty(m, sel);
    const isActive = qty > 0;
    const showColorChip = (m.colors || []).length > 0;
    return (
      <TableRow
        key={sk}
        className={isActive ? "bg-emerald-50/30" : ""}
        data-testid={`mat-row-${tid}`}
      >
        <TableCell className="text-black font-medium align-top py-2">
          {m.name}
          {showColorChip && (
            <span
              className={`ml-2 inline-block px-1.5 py-0 rounded-sm font-mono text-[10px] align-middle border ${
                color === MAT_COLOR
                  ? "bg-zinc-100 border-zinc-200 text-zinc-700"
                  : "bg-black text-white border-black"
              }`}
              data-testid={`mat-color-chip-${tid}`}
            >
              {color}
            </span>
          )}
        </TableCell>
        <TableCell className="text-center text-zinc-700 font-mono text-xs align-top py-2">
          {m.unit || "—"}
        </TableCell>
        <TableCell className="text-right text-black font-mono text-xs align-top py-2">
          {formatRON(unitPrice)}
        </TableCell>
        <TableCell className="align-top py-2">
          {hasWidth ? (
            renderWidthInput(m, sk, tid)
          ) : (
            <Input
              type="number"
              min="0"
              step="0.01"
              value={sel.quantity ?? ""}
              onChange={(e) => updateSel(sk, { quantity: e.target.value })}
              placeholder="0"
              className="h-8 rounded-sm border-zinc-300 focus-visible:ring-black text-sm"
              data-testid={`mat-qty-${tid}`}
            />
          )}
        </TableCell>
        <TableCell className="align-top py-2">
          <Textarea
            value={sel.notes ?? ""}
            onChange={(e) => updateSel(sk, { notes: e.target.value })}
            placeholder="—"
            rows={1}
            className="rounded-sm border-zinc-300 focus-visible:ring-black resize-y min-h-[32px] text-sm py-1"
            data-testid={`mat-notes-${tid}`}
          />
        </TableCell>
        <TableCell className="text-right align-top py-2">
          {isActive ? (
            <span className="font-mono text-sm font-semibold text-black">
              {formatRON(unitPrice * qty)}
            </span>
          ) : (
            <span className="text-zinc-400 font-mono text-xs">—</span>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div className="p-6 md:p-10" data-testid="calculator-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">
            Generare factura
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-black">
            Calculator
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-2xl">
            Introdu cantitatile direct in lista cu materialele tale. La materialele cu latime,
            poti introduce lungimi si nr. de coli sau m² direct.
          </p>
        </div>
        {Object.keys(selections).length > 0 && (
          <Button
            variant="outline"
            onClick={resetSelections}
            className="rounded-sm border-zinc-300 h-10"
            data-testid="reset-selections-button"
          >
            <RotateCcw size={14} className="mr-2" />
            Reseteaza
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[2.2fr_1fr] gap-6 items-start">
        {/* Materials list */}
        <div className="border border-zinc-200 rounded-sm bg-white">
          <div className="p-4 border-b border-zinc-200 bg-white space-y-2">
            <ColorPicker
              colors={allColors}
              selected={selectedColors}
              onChange={setSelectedColors}
              testIdPrefix="calc-color"
            />
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cauta material..."
                className="rounded-sm border-zinc-300 focus-visible:ring-black pl-9 h-9"
                data-testid="materials-search-input"
              />
            </div>
          </div>
          <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-zinc-50">
                <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                  <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">
                    Material
                  </TableHead>
                  <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-center w-14">
                    U.M.
                  </TableHead>
                  <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-right w-24">
                    Pret
                  </TableHead>
                  <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide w-64 min-w-[13rem]">
                    Cantitate
                  </TableHead>
                  <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide w-44">
                    Note
                  </TableHead>
                  <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-right w-24">
                    Total
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grouped.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-zinc-500 py-8">
                      Niciun material gasit
                    </TableCell>
                  </TableRow>
                ) : (
                  grouped.map((group) => (
                    <React.Fragment key={group.id}>
                      <TableRow className="bg-zinc-100/70 hover:bg-zinc-100/70">
                        <TableCell colSpan={6} className="py-1.5">
                          <div className="flex items-center gap-2">
                            <Folder size={13} className="text-zinc-700" />
                            <span className="font-mono uppercase text-[11px] tracking-widest text-zinc-700 font-bold">
                              {group.name}
                            </span>
                            <span className="font-mono text-[10px] text-zinc-500">
                              ({group.items.length})
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                      {group.items.flatMap((m) =>
                        appColors(m).map((color) => renderMaterialRow(m, color))
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Invoice preview */}
        <div className="space-y-4">
          <div className="border border-zinc-200 rounded-sm bg-white p-5">
            <h2 className="font-display text-2xl font-bold tracking-tight text-black mb-4">
              Date client
            </h2>
            {/* Document number — auto by default; user can override. */}
            <div className="mb-4 pb-4 border-b border-dashed border-zinc-200">
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Numar document
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 whitespace-nowrap">
                  Seria COS ·
                </span>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={customNumber}
                  onChange={(e) => setCustomNumber(e.target.value)}
                  placeholder={
                    suggestedNumber != null
                      ? `auto: ${String(suggestedNumber).padStart(3, "0")}`
                      : "auto"
                  }
                  className="rounded-sm border-zinc-300 focus-visible:ring-black h-9 w-32 font-mono"
                  data-testid="invoice-number-input"
                />
                {customNumber && (
                  <button
                    type="button"
                    onClick={() => setCustomNumber("")}
                    className="font-mono uppercase text-[10px] tracking-wider text-zinc-500 hover:text-black"
                    data-testid="invoice-number-clear"
                    title="Foloseste numar auto"
                  >
                    auto
                  </button>
                )}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                Las gol pentru numar auto. Daca alegi unul mai mare, sequentia se sincronizeaza automat.
              </div>
            </div>
            {/* Agent — auto-filled from profile, editable + saveable for next time */}
            <div className="mb-4 pb-4 border-b border-dashed border-zinc-200">
              <Label className="text-black font-semibold uppercase text-[10px] tracking-wide flex items-center gap-1.5">
                <UserCog size={12} /> Facturat de (agent)
              </Label>
              <div className="flex gap-1 mt-1">
                <Input
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder="Numele tau"
                  className="rounded-sm border-zinc-300 focus-visible:ring-black flex-1"
                  data-testid="agent-name-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={saveAgentName}
                  disabled={savingAgent || agentName === (user?.agent_name || "")}
                  className="rounded-sm border-zinc-300 h-10 px-3"
                  title="Salveaza pentru a fi pre-completat la urmatoarele facturi"
                  data-testid="agent-name-save"
                >
                  <Save size={12} />
                </Button>
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 font-mono">
                Apare pe document: "Facturat de: {agentName || "—"}"
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Nume
                </Label>
                <ClientAutocomplete
                  value={clientLastName}
                  onChange={setClientLastName}
                  onPick={pickClient}
                  placeholder="Popescu"
                  testid="client-last-name"
                />
              </div>
              <div>
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Prenume
                </Label>
                <Input
                  value={clientFirstName}
                  onChange={(e) => setClientFirstName(e.target.value)}
                  placeholder="Ion"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="client-first-name"
                />
              </div>
              <div>
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  C.I.F.
                </Label>
                <Input
                  value={clientCui}
                  onChange={(e) => setClientCui(e.target.value)}
                  placeholder="RO12345678"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="client-cui"
                />
              </div>
              <div>
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  CNP
                </Label>
                <Input
                  value={clientCnp}
                  onChange={(e) => setClientCnp(e.target.value)}
                  placeholder="1980712123456"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="client-cnp"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Adresa
                </Label>
                <Input
                  value={clientAddress}
                  onChange={(e) => setClientAddress(e.target.value)}
                  placeholder="Str. Exemplu nr. 12, Bucuresti"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="client-address"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Telefon
                </Label>
                <Input
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="07xx xxx xxx"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="client-phone"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Nr. Reg. Com.
                </Label>
                <Input
                  value={clientRegCom}
                  onChange={(e) => setClientRegCom(e.target.value)}
                  placeholder="J33/xxxx/202x"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="client-reg-com"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Numele delegatului
                </Label>
                <Input
                  value={delegateName}
                  onChange={(e) => setDelegateName(e.target.value)}
                  placeholder="Popescu Ion"
                  className="mt-1 rounded-sm border-zinc-300 focus-visible:ring-black"
                  data-testid="delegate-name"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <Label className="text-black font-semibold uppercase text-[10px] tracking-wide flex items-center gap-1.5">
                  <Percent size={12} /> Reducere
                </Label>
                <div className="relative mt-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={discountPct}
                    onChange={(e) => setDiscountPct(e.target.value)}
                    placeholder="0"
                    className="rounded-sm border-zinc-300 focus-visible:ring-black pr-8"
                    data-testid="invoice-discount-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-sm">
                    %
                  </span>
                </div>
              </div>
              <div>
                <Label className="text-black font-semibold uppercase text-xs tracking-wide flex items-center gap-1.5">
                  <Wallet size={12} /> Avans
                </Label>
                <div className="relative mt-2">
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={advance}
                    onChange={(e) => setAdvance(e.target.value)}
                    placeholder="0"
                    className="rounded-sm border-zinc-300 focus-visible:ring-black pr-12"
                    data-testid="invoice-advance-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 font-mono text-xs">
                    RON
                  </span>
                </div>
              </div>
            </div>
            <label
              className="mt-4 inline-flex items-center gap-2.5 cursor-pointer select-none px-3 py-2 rounded-sm border border-zinc-300 bg-white hover:border-zinc-500 transition"
              data-testid="invoice-include-codes-toggle"
            >
              <input
                type="checkbox"
                checked={includeCodes}
                onChange={(e) => setIncludeCodes(e.target.checked)}
                className="h-4 w-4 accent-black"
                data-testid="invoice-include-codes-checkbox"
              />
              <span className="font-mono uppercase text-[11px] tracking-wider text-black font-semibold">
                Include coduri
              </span>
              <span className="font-mono text-[10px] text-zinc-500">
                (coloana Cod pe factura)
              </span>
            </label>
          </div>

          <div className="border border-zinc-200 rounded-sm bg-white p-5">
            <h2 className="font-display text-2xl font-bold tracking-tight text-black mb-3">
              Factura
            </h2>
            <div className="border border-zinc-200 rounded-sm overflow-hidden">
              <div className="max-h-[220px] overflow-y-auto">
                <Table>
                  <TableHeader className="sticky top-0 z-10 bg-zinc-50">
                    <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                      <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">
                        Material
                      </TableHead>
                      <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-right">
                        Cant.
                      </TableHead>
                      <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-right">
                        Total
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoice.rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-zinc-500 py-6 text-sm">
                          Selecteaza materiale din stanga (cantitate &gt; 0)
                        </TableCell>
                      </TableRow>
                    ) : (
                      invoice.rows.map((r) => (
                        <TableRow key={`${r.material_id}::${r.color || "Mat"}`}>
                          <TableCell className="text-black text-sm">
                            {r.name}
                            {r.color ? (
                              <span className="ml-1.5 inline-block px-1.5 py-0 rounded-sm bg-zinc-100 border border-zinc-200 font-mono text-[10px] text-zinc-700 align-middle">
                                {r.color}
                              </span>
                            ) : null}
                            {r.unit ? (
                              <span className="text-zinc-400 ml-1 text-xs">· {r.unit}</span>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm text-black">
                            {r.quantity}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm font-semibold text-black">
                            {formatRON(r.line_total)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-zinc-200 space-y-2">
              {invoice.discount_pct > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="font-mono uppercase text-[11px] tracking-widest text-zinc-500">
                      Subtotal
                    </span>
                    <span className="font-mono text-sm text-zinc-700" data-testid="invoice-subtotal">
                      {formatRON(invoice.subtotal)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-mono uppercase text-[11px] tracking-widest text-zinc-500">
                      Reducere {invoice.discount_pct}%
                    </span>
                    <span
                      className="font-mono text-sm text-red-600"
                      data-testid="invoice-discount-value"
                    >
                      -{formatRON(invoice.discount_value)}
                    </span>
                  </div>
                </>
              )}
              {invoice.advance > 0 && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="font-mono uppercase text-[11px] tracking-widest text-zinc-500">
                      Total factura
                    </span>
                    <span
                      className="font-mono text-sm text-zinc-700"
                      data-testid="invoice-total-before-advance"
                    >
                      {formatRON(invoice.total)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-mono uppercase text-[11px] tracking-widest text-zinc-500">
                      Avans achitat
                    </span>
                    <span
                      className="font-mono text-sm text-red-600"
                      data-testid="invoice-advance-value"
                    >
                      -{formatRON(invoice.advance)}
                    </span>
                  </div>
                </>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-zinc-100">
                <span className="font-mono uppercase text-xs tracking-widest text-zinc-600">
                  {invoice.advance > 0 ? "Rest de plata" : "Total de plata"}
                </span>
                <span
                  className="font-display text-2xl font-black tracking-tighter text-black"
                  data-testid="invoice-total"
                >
                  {formatRON(invoice.due)}
                </span>
              </div>
            </div>

            <Button
              onClick={downloadPdf}
              disabled={downloading || !invoice.hasSelected}
              className="w-full mt-5 rounded-sm bg-black hover:bg-zinc-800 text-white h-11"
              data-testid="download-pdf-button"
            >
              <FileDown size={18} className="mr-2" />
              {downloading ? "Se genereaza..." : "Descarca factura (DOCX)"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
